import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchPricingPlans, PricingResponse, TrialInfo, SubscriptionPlan } from '../services/api';

interface PricingContextType {
  pricingData: PricingResponse | null;
  trialInfo: TrialInfo | null;
  plans: SubscriptionPlan[];
  activityCost: number;
  loading: boolean;
  error: string | null;
  refetchPricing: () => Promise<void>;
}

const PricingContext = createContext<PricingContextType | undefined>(undefined);

interface PricingProviderProps {
  children: ReactNode;
}

export const PricingProvider: React.FC<PricingProviderProps> = ({ children }) => {
  const [pricingData, setPricingData] = useState<PricingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPricing = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('💰 PricingContext: Loading pricing data...');
      const data = await fetchPricingPlans();
      setPricingData(data);
      const pricingHidden = data.pricing_hidden === true;
      const plansCount = pricingHidden ? 0 : data.plans.length;
      const activityCost = pricingHidden ? undefined : data.trial_info.activity_cost;
      const trialActivities = pricingHidden ? undefined : data.trial_info.trial_activities;
      console.log('✅ PricingContext: Pricing data loaded successfully', {
        pricingHidden,
        plansCount,
        activityCost,
        trialActivities,
      });
    } catch (err) {
      console.error('❌ PricingContext: Error loading pricing data:', err);
      setError('Failed to load pricing information');
      setPricingData(null);
    } finally {
      setLoading(false);
    }
  };

  const refetchPricing = async () => {
    await loadPricing();
  };

  useEffect(() => {
    loadPricing();
  }, []);

  // Extract helper values
  const trialInfo = pricingData?.trial_info || null;
  const plans = pricingData?.plans || [];
  const activityCost = trialInfo?.activity_cost || 5; // Fallback to 5 if not loaded yet

  const value: PricingContextType = {
    pricingData,
    trialInfo,
    plans,
    activityCost,
    loading,
    error,
    refetchPricing,
  };

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = (): PricingContextType => {
  const context = useContext(PricingContext);
  if (context === undefined) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
};